import {
  DOMAIN_BRANDING_OVERRIDES,
  FSDS_DOMAIN_BRANDING,
  NON_FSDS_FALLBACK_BRANDING,
  ResolvedDomainBrandingConfig,
} from '@/configs/domain-branding.config'

const FSDS_DOMAIN = 'fullstackdatascience.com'

const LOCAL_DEV_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0'])

/**
 * Derive a human-readable brand name from a hostname.
 * e.g. "my-academy.example.com" → "My Academy"
 *      "datahub.vn"             → "Datahub"
 */
export function hostnameToBrandName(hostname: string): string {
  if (!hostname) return ''

  // Take the first meaningful subdomain segment (skip "www")
  const parts = hostname.split('.')
  const meaningful = parts.find(p => p !== 'www' && p.length > 0) || parts[0] || hostname

  // Convert kebab-case / dots to spaces and capitalize each word
  return meaningful
    .replace(/[-_.]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim()
}

function isLocalOrDevHostname(hostname: string): boolean {
  return LOCAL_DEV_HOSTNAMES.has(hostname)
}

/** Bare localhost / loopback — not tenant.localhost subdomains. */
export function isBareLocalDevHostname(host?: string | null): boolean {
  const hostname = normalizeHostname(host)
  return isLocalOrDevHostname(hostname)
}

/** non-fsds-only nav items are also shown on bare localhost for local development. */
export function shouldShowNonFsdsNavItems(isFsdsDomain: boolean, hostname?: string | null): boolean {
  return !isFsdsDomain || isBareLocalDevHostname(hostname)
}

export function normalizeHostname(host?: string | null): string {
  if (!host) {
    return ''
  }

  const normalizedHost = host
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .split('?')[0]
    .split('#')[0]

  if (!normalizedHost || normalizedHost.startsWith('[') || normalizedHost.includes(']')) {
    return ''
  }

  const hostname = normalizedHost.split(':')[0].replace(/\.+$/, '')

  if (!hostname || !/^[a-z0-9.-]+$/.test(hostname)) {
    return ''
  }

  if (hostname === `www.${FSDS_DOMAIN}`) {
    return FSDS_DOMAIN
  }

  return hostname
}

export function isFsdsHostname(host?: string | null): boolean {
  const hostname = normalizeHostname(host)
  return hostname === FSDS_DOMAIN || hostname.endsWith(`.${FSDS_DOMAIN}`) || isLocalOrDevHostname(hostname)
}

export function resolveDomainBranding(host?: string | null): ResolvedDomainBrandingConfig {
  const hostname = normalizeHostname(host)

  if (!hostname || isFsdsHostname(hostname)) {
    return {
      ...FSDS_DOMAIN_BRANDING,
      hostname,
      isFsdsDomain: true,
    }
  }

  const override = DOMAIN_BRANDING_OVERRIDES[hostname] ?? {}

  // Derive a human-readable name from the hostname so metadata is never
  // stuck showing a generic fallback like "Data Platform".
  const derivedName = override.brandName || hostnameToBrandName(hostname) || NON_FSDS_FALLBACK_BRANDING.brandName

  return {
    ...NON_FSDS_FALLBACK_BRANDING,
    ...override,
    brandName: derivedName,
    meta: {
      ...NON_FSDS_FALLBACK_BRANDING.meta,
      siteName: derivedName,
      appTitle: derivedName,
      publisherName: derivedName,
      ...override.meta,
    },
    icons: {
      ...NON_FSDS_FALLBACK_BRANDING.icons,
      ...override.icons,
    },
    hostname,
    isFsdsDomain: false,
  }
}
